import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";

mermaid.initialize({
  startOnLoad: false,
  theme: "neutral",
  securityLevel: "loose",
  suppressErrorRendering: true, // never inject the "Syntax error" bomb graphic into the DOM
  sequence: { useMaxWidth: true, wrap: true },
  fontFamily: "IBM Plex Mono, monospace",
});

let _uid = 0;
// Serialize all mermaid.render calls — mermaid is a singleton and concurrent
// renders (e.g. React StrictMode double-invoke) race and leave containers empty.
let _chain = Promise.resolve();
function renderQueued(id, chart) {
  const run = _chain.then(() => mermaid.render(id, chart));
  _chain = run.catch(() => {});
  return run;
}

export function MermaidDiagram({ chart }) {
  const ref = useRef(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setErr(false);
    if (!chart || !chart.trim()) return;
    const id = `mmd-${Date.now()}-${_uid++}`;
    (async () => {
      try {
        const { svg } = await renderQueued(id, chart);
        if (cancelled) return;
        if (ref.current) ref.current.innerHTML = svg;
        setErr(false);
      } catch {
        // Invalid diagram: suppressErrorRendering keeps the bomb out of the DOM;
        // we show a plain <pre> fallback instead.
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [chart]);

  if (!chart || !chart.trim()) {
    return <div className="text-xs text-zinc-400 italic py-3">Not handled by this parser.</div>;
  }
  if (err) {
    return (
      <pre className="text-[11px] bg-zinc-50 border border-zinc-200 rounded p-3 overflow-x-auto text-zinc-600 whitespace-pre-wrap" data-testid="mermaid-fallback">
        {chart}
      </pre>
    );
  }
  return <div ref={ref} className="overflow-x-auto py-2" data-testid="mermaid-svg" />;
}
